/**
 * ExpandableChatMessages
 *
 * Container that wires the messaging store to ConversationList + MessageThread
 * for use inside GlobalExpandableChat. Two-view pattern:
 *   1. Conversation list (default)
 *   2. Message thread (when a conversation is selected)
 */

import React, { useEffect, useCallback, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConversationList } from '@/components/messages/ConversationList';
import { MessageThread } from '@/components/messages/MessageThread';
import { ConvertToGroupModal } from '@/components/messages/ConvertToGroupModal';
import {
  useConversations,
  useActiveConversation,
  useActiveConversationId,
  useMessages,
  usePendingMessages,
  useTypingIndicator,
  useMessagingLoading,
  useHasMoreMessages,
  useMessagingActions,
  useComposingState,
  useMessagingWebSocketStatus,
  setupMessagingWebSocketListeners,
  isFailedPendingMessageError,
} from '@/stores/messaging';
import { useAuth } from '@/stores/auth/auth-store';
import { getErrorMessageOrFallback } from '@/utils/errors';
import { logger } from '@/utils/logger';

const logAsyncError = (operation: string, error: unknown): void => {
  logger.warn('ExpandableChatMessages action failed', {
    operation,
    error: getErrorMessageOrFallback(error, 'Unexpected error'),
  });
};

export const ExpandableChatMessages: React.FC = () => {
  const { user } = useAuth();

  // Store selectors
  const conversations = useConversations();
  const activeConversation = useActiveConversation();
  const activeConversationId = useActiveConversationId();
  const messages = useMessages();
  const pendingMessages = usePendingMessages();
  const typingUserIds = useTypingIndicator(activeConversationId ?? '');
  const {
    isLoadingConversations,
    isLoadingMessages,
    isSendingMessage,
    isLoadingMore,
  } = useMessagingLoading();
  const { isSettingUpWebSocketListeners } = useMessagingWebSocketStatus();
  const hasMoreMessages = useHasMoreMessages();
  const {
    fetchConversations,
    setActiveConversation,
    loadMoreMessages,
    sendMessage,
    retryPendingMessage,
    removePendingMessage,
    editMessage,
    deleteMessage,
    archiveConversation,
    unarchiveConversation,
    muteConversation,
    unmuteConversation,
    emitTypingStart,
    emitTypingStop,
    startComposing,
    cancelComposing,
    addComposingRecipient,
    removeComposingRecipient,
    sendComposingMessage,
  } = useMessagingActions();
  const { isComposing, composingRecipients } = useComposingState();

  // Local view state — whether we're showing a thread or the list
  const [showThread, setShowThread] = useState(false);
  const [showConvertToGroupModal, setShowConvertToGroupModal] = useState(false);
  const hasInitializedWebSocketListenersRef = useRef(false);

  // Fetch conversations + set up WebSocket on mount
  useEffect(() => {
    void fetchConversations().catch((error) => {
      logAsyncError('fetch_conversations', error);
    });
  }, [fetchConversations]);

  useEffect(() => {
    if (
      hasInitializedWebSocketListenersRef.current ||
      isSettingUpWebSocketListeners
    ) {
      return;
    }

    hasInitializedWebSocketListenersRef.current = true;
    setupMessagingWebSocketListeners();
  }, [isSettingUpWebSocketListeners]);

  // Handlers
  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      void setActiveConversation(conversationId)
        .then(() => setShowThread(true))
        .catch((error) => {
          logAsyncError('select_conversation', error);
        });
    },
    [setActiveConversation],
  );

  const handleBack = useCallback(() => {
    if (isComposing) {
      cancelComposing();
    }
    setShowThread(false);
  }, [cancelComposing, isComposing]);

  const handleNewConversation = useCallback(() => {
    startComposing();
    setShowThread(true);
  }, [startComposing]);

  const handleSendMessage = useCallback(
    async (content: string, replyToId?: string) => {
      try {
        await sendMessage(content, replyToId);
      } catch (error) {
        if (isFailedPendingMessageError(error)) return;
        throw error;
      }
    },
    [sendMessage],
  );

  const handleComposingSend = useCallback(
    async (content: string) => {
      const conversation = await sendComposingMessage(content);
      if (!conversation) throw new Error('Failed to send message');
      // Stay in thread view with the new conversation
    },
    [sendComposingMessage],
  );

  const handleEditMessage = useCallback(
    async (messageId: string, content: string) => {
      await editMessage(messageId, content);
    },
    [editMessage],
  );

  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      await deleteMessage(messageId);
    },
    [deleteMessage],
  );

  const handleArchive = useCallback(async () => {
    if (activeConversationId) {
      await archiveConversation(activeConversationId);
    }
  }, [activeConversationId, archiveConversation]);

  const handleUnarchive = useCallback(async () => {
    if (activeConversationId) {
      await unarchiveConversation(activeConversationId);
    }
  }, [activeConversationId, unarchiveConversation]);

  const handleMute = useCallback(
    async (mutedUntil: Date) => {
      if (activeConversationId) {
        await muteConversation(activeConversationId, mutedUntil);
      }
    },
    [activeConversationId, muteConversation],
  );

  const handleUnmute = useCallback(async () => {
    if (activeConversationId) {
      await unmuteConversation(activeConversationId);
    }
  }, [activeConversationId, unmuteConversation]);

  const handleTypingStart = useCallback(() => {
    if (activeConversationId) emitTypingStart(activeConversationId);
  }, [activeConversationId, emitTypingStart]);

  const handleTypingStop = useCallback(() => {
    if (activeConversationId) emitTypingStop(activeConversationId);
  }, [activeConversationId, emitTypingStop]);

  const handleCancelComposing = useCallback(() => {
    cancelComposing();
    setShowThread(false);
  }, [cancelComposing]);

  const handleOpenExistingConversation = useCallback(
    (conversation: { id: string }) => {
      cancelComposing();
      void setActiveConversation(conversation.id)
        .then(() => setShowThread(true))
        .catch((error) => {
          logAsyncError('open_existing_conversation', error);
        });
    },
    [cancelComposing, setActiveConversation],
  );

  const handleConvertToGroup = useCallback(() => {
    setShowConvertToGroupModal(true);
  }, []);

  // --- Render ---

  if (showThread || isComposing) {
    return (
      <div className="flex flex-col h-full">
        {/* Back button bar */}
        <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={handleBack}
            aria-label="Back to conversations"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs font-medium text-muted-foreground truncate">
            {isComposing
              ? 'New Message'
              : activeConversation?.isGroup
                ? activeConversation.groupName ?? 'Group'
                : activeConversation?.participant
                  ? `${activeConversation.participant.firstName ?? ''} ${activeConversation.participant.lastName ?? ''}`.trim() || activeConversation.participant.username
                  : 'Conversation'}
          </span>
        </div>

        {/* MessageThread fills remaining space */}
        <div className="flex-1 min-h-0">
          <MessageThread
            conversation={activeConversation}
            messages={messages}
            pendingMessages={pendingMessages}
            typingUserIds={typingUserIds}
            currentUserId={user?.id ?? ''}
            isLoading={isLoadingMessages}
            isLoadingMore={isLoadingMore}
            hasMore={hasMoreMessages}
            isSending={isSendingMessage}
            onSendMessage={isComposing ? handleComposingSend : handleSendMessage}
            onRetryPendingMessage={async (tempId) => {
              await retryPendingMessage(tempId);
            }}
            onRemovePendingMessage={removePendingMessage}
            onEditMessage={handleEditMessage}
            onDeleteMessage={handleDeleteMessage}
            onLoadMore={loadMoreMessages}
            onTypingStart={handleTypingStart}
            onTypingStop={handleTypingStop}
            onArchive={handleArchive}
            onUnarchive={handleUnarchive}
            onMute={handleMute}
            onUnmute={handleUnmute}
            isComposing={isComposing}
            composingRecipients={composingRecipients}
            onAddComposingRecipient={addComposingRecipient}
            onRemoveComposingRecipient={removeComposingRecipient}
            onCancelComposing={handleCancelComposing}
            onOpenExistingConversation={handleOpenExistingConversation}
            onConvertToGroup={handleConvertToGroup}
          />
        </div>

        {/* Convert to group modal */}
        {activeConversation && !activeConversation.isGroup && (
          <ConvertToGroupModal
            open={showConvertToGroupModal}
            onOpenChange={setShowConvertToGroupModal}
            conversation={activeConversation}
          />
        )}
      </div>
    );
  }

  // Conversation list view
  return (
    <div className="flex flex-col h-full">
      <ConversationList
        conversations={conversations}
        activeConversationId={activeConversationId}
        isLoading={isLoadingConversations}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
      />
    </div>
  );
};
